import { api } from './axios';

/**
 * API клиент для работы с парами участников (Battle Pairs)
 * 
 * @module battlePairsApi
 */

/**
 * Получить список пар раунда с пагинацией
 * @param {Object} params
 * @param {string} params.roundId - UUID раунда
 * @param {number} params.page - Номер страницы (0-indexed)
 * @param {number} params.size - Размер страницы (max 100)
 * @param {string} params.sortBy - Поле сортировки (по умолчанию 'pairNumber')
 * @param {string} params.direction - Направление ('ASC' или 'DESC')
 * @returns {Promise<AxiosResponse>}
 */
export const getPairsByRound = ({ roundId, page = 0, size = 20, sortBy = 'pairNumber', direction = 'ASC' }) => {
  return api.get('/battle_pairs', {
    params: { roundId, page, size, sortBy, direction }
  });
};

/**
 * Получить пару по ID
 * @param {string} id - UUID пары
 * @returns {Promise<AxiosResponse>}
 */
export const getPairById = (id) => {
  return api.get(`/battle_pairs/${id}`);
};

/**
 * Получить результаты голосования по паре: треки обоих участников,
 * средние баллы, количество голосов, победитель.
 * @param {string} id - UUID пары
 * @returns {Promise<AxiosResponse>} PairResultsResponse
 */
export const getPairResults = (id) => {
  return api.get(`/battle_pairs/${id}/results`);
};

/**
 * Создать новую пару
 * @param {Object} data
 * @param {string} data.roundId - UUID раунда
 * @param {string} data.firstPersonId - UUID первого участника
 * @param {string} data.secondPersonId - UUID второго участника
 * @param {number} data.pairNumber - Номер пары
 * @param {string} [data.notes] - Заметки
 * @returns {Promise<AxiosResponse>}
 */
export const createPair = (data) => {
  return api.post('/battle_pairs', data);
};

/**
 * Обновить пару
 * @param {string} id - UUID пары
 * @param {Object} data
 * @param {string} [data.firstPersonId] - UUID первого участника
 * @param {string} [data.secondPersonId] - UUID второго участника
 * @param {number} [data.pairNumber] - Номер пары
 * @param {string} [data.notes] - Заметки
 * @returns {Promise<AxiosResponse>}
 */
export const updatePair = (id, data) => {
  return api.put(`/battle_pairs/${id}`, data);
};

/**
 * Удалить пару
 * @param {string} id - UUID пары
 * @returns {Promise<AxiosResponse>}
 */
export const deletePair = (id) => {
  return api.delete(`/battle_pairs/${id}`);
};

/**
 * Объявить победителя пары
 * @param {string} id - UUID пары
 * @param {string} winnerId - UUID победителя
 * @returns {Promise<AxiosResponse>}
 */
export const setWinner = (id, winnerId) => {
  return api.post(`/battle_pairs/${id}/winner`, { winnerId });
};

/**
 * Изменить статус пары
 * @param {string} id - UUID пары
 * @param {string} status - Новый статус ('PENDING', 'ACTIVE', 'FINISHED', 'DISQUALIFIED')
 * @returns {Promise<AxiosResponse>}
 */
export const updatePairStatus = (id, status) => {
  return api.patch(`/battle_pairs/${id}/status`, { status });
};

/**
 * Активировать пару
 * @param {string} id - UUID пары
 * @returns {Promise<AxiosResponse>}
 */
export const activatePair = (id) => {
  return api.post(`/battle_pairs/${id}/activate`);
};

/**
 * Завершить пару
 * @param {string} id - UUID пары
 * @param {string} winnerId - UUID победителя
 * @returns {Promise<AxiosResponse>}
 */
export const finishPair = (id, winnerId) => {
  return api.post(`/battle_pairs/${id}/finish`, { winnerId });
};

/**
 * Дисквалифицировать пару
 * @param {string} id - UUID пары
 * @returns {Promise<AxiosResponse>}
 */
export const disqualifyPair = (id) => {
  return api.post(`/battle_pairs/${id}/disqualify`);
};

/**
 * Найти пары по участнику
 * @param {string} personId - UUID участника
 * @returns {Promise<AxiosResponse>}
 */
export const getPairsByPerson = (personId) => {
  return api.get(`/battle_pairs/person/${personId}`);
};

/**
 * Найти пару участника в раунде
 * @param {string} roundId - UUID раунда
 * @param {string} personId - UUID участника
 * @returns {Promise<AxiosResponse>}
 */
export const getPairByRoundAndPerson = (roundId, personId) => {
  return api.get(`/battle_pairs/round/${roundId}/person/${personId}`);
};

/**
 * Сгенерировать пары для раунда (жеребьёвка)
 * @param {string} roundId - UUID раунда
 * @param {Object} data
 * @param {string} data.drawType - Тип жеребьёвки ('RANDOM', 'SEEDED')
 * @param {boolean} [data.shuffle=true] - Перемешивать ли участников
 * @returns {Promise<AxiosResponse>}
 */
export const generatePairsForRound = (roundId, data) => {
  return api.post(`/battle_pairs/round/${roundId}/generate`, data);
};

const battlePairsApi = {
  getPairsByRound,
  getPairById,
  getPairResults,
  createPair,
  updatePair,
  deletePair,
  setWinner,
  updatePairStatus,
  activatePair,
  finishPair,
  disqualifyPair,
  getPairsByPerson,
  getPairByRoundAndPerson,
  generatePairsForRound
};

export default battlePairsApi;

